import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useTopBarStore } from "@/stores/topBar";
import { useWorkspacesStore } from "@/stores/workspaces";

const Login = () => import("@/views/login.vue");
const EmptyLayout = () => import("@/views/EmptyLayout.vue");

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			path: "/",
			meta: { authRequired: true, title: "Dashboard" },
			redirect: "/workspaces",
		},
		{
			path: "/workspaces",
			meta: { authRequired: true, title: "Workspaces" },
			component: () => import("@/views/workspaces.vue"),
			name: "Workspaces",
		},
		{
			path: "/live/owner/:orgName/w/:workspaceName/products/:productName/:productVersion/preview",
			meta: {
				authRequired: false,
				title: ":productName – Live Preview",
				hideSideNav: true,
				showEditorMenuBar: false,
			},
			name: "Live Preview",
			component: () =>
				import(
					"@/views/owner/[owner]/w/[workspace]/products/[product]/[version]/preview.vue"
				),
		},
		{
			path: "/login",
			meta: { title: "Login" },
			component: Login,
			name: "Login",
		},
		{
			path: "/login/callback",
			meta: { title: "Login" },
			component: Login,
			name: "Callback",
		},
		{
			path: "/settings",
			meta: { authRequired: true, title: "Settings" },
			component: () => import("@/views/settings.vue"),
			name: "Settings",
		},
		{
			path: "/team",
			meta: {
				authRequired: true,
				title: "Manage Team",
				permissionRequired: "admin",
			},
			component: () => import("@/views/team.vue"),
			name: "Team",
		},
		{
			path: "/owner/:orgName/w/:workspaceName",
			meta: { authRequired: true, title: ":workspaceName – Workspace" },
			component: () => import("@/views/owner/[owner]/w/[workspace].vue"),
			name: "Workspace",
			children: [
				{
					path: "",
					meta: { authRequired: true, title: ":workspaceName – Workspace" },
					name: "Workspace Overview",
					component: () =>
						import("@/views/owner/[owner]/w/[workspace]/index.vue"),
				},
				{
					path: "files",
					meta: {
						authRequired: true,
						title: "Workspace Files",
						permissionRequired: "write",
					},
					name: "Workspace Files",
					component: () =>
						import("@/views/owner/[owner]/w/[workspace]/files.vue"),
				},
				{
					path: "projects",
					meta: { authRequired: true, title: "Projects" },
					name: "Projects",
					component: EmptyLayout,
					children: [
						{
							path: "",
							meta: { authRequired: true, title: "Projects" },
							name: "Projects List",
							component: () =>
								import(
									"@/views/owner/[owner]/w/[workspace]/projects/index.vue"
								),
						},
						{
							path: ":projectName",
							meta: { authRequired: true, title: ":projectName" },
							name: "Project",
							component: EmptyLayout,
							children: [
								{
									path: "",
									meta: { authRequired: true, title: ":projectName" },
									name: "Project Overview",
									component: () =>
										import(
											"@/views/owner/[owner]/w/[workspace]/projects/[project]/index.vue"
										),
								},
								{
									path: "editor",
									meta: {
										authRequired: true,
										hideSideNav: true,
										showEditorMenuBar: true,
										title: ":projectName - Editor",
										permissionRequired: "write",
									},
									name: "Editor",
									component: () =>
										import(
											"@/views/owner/[owner]/w/[workspace]/projects/[project]/editor.vue"
										),
								},
							],
						},
					],
				},
				{
					path: "products",
					meta: { authRequired: true, title: "Products" },
					name: "Products",
					component: EmptyLayout,
					children: [
						{
							path: "",
							meta: { authRequired: true, title: "Products" },
							name: "Products List",
							component: () =>
								import(
									"@/views/owner/[owner]/w/[workspace]/products/index.vue"
								),
						},
						{
							path: ":productName/:productVersion",
							meta: { authRequired: true, title: ":productName" },
							name: "Product",
							component: () =>
								import(
									"@/views/owner/[owner]/w/[workspace]/products/[product]/[version]/[version].vue"
								),
							children: [
								{
									path: "",
									meta: {
										authRequired: true,
										title: ":productName – Overview",
									},
									name: "Product Overview",
									component: () =>
										import(
											"@/views/owner/[owner]/w/[workspace]/products/[product]/[version]/index.vue"
										),
								},
								{
									path: "configurability",
									meta: {
										authRequired: true,
										title: ":productName – Configurability",
									},
									name: "Product Configurability",
									component: () =>
										import(
											"@/views/owner/[owner]/w/[workspace]/products/[product]/[version]/configurability.vue"
										),
								},
								{
									path: "preview",
									meta: {
										authRequired: true,
										title: ":productName – Live Preview",
									},
									name: "Product Live Preview",
									component: () =>
										import(
											"@/views/owner/[owner]/w/[workspace]/products/[product]/[version]/preview.vue"
										),
								},

								{
									path: "implementation",
									meta: {
										authRequired: true,
										title: ":productName – Implementation",
									},
									name: "Product Implementation",
									component: () =>
										import(
											"@/views/owner/[owner]/w/[workspace]/products/[product]/[version]/implementation.vue"
										),
								},
								{
									path: "studio",
									meta: {
										authRequired: true,
										title: ":productName – Virtual Studio",
									},
									name: "Virtual Studio",
									component: () =>
										import(
											"@/views/owner/[owner]/w/[workspace]/products/[product]/[version]/studio.vue"
										),
								},
							],
						},
					],
				},
				{
					path: "editor",
					meta: {
						authRequired: true,
						title: "glTF Editor",
						permissionRequired: "write",
					},
					name: "Editor Intro",
					component: () =>
						import("@/views/owner/[owner]/w/[workspace]/editor.vue"),
				},
				{
					path: "studio",
					meta: {
						authRequired: true,
						title: "Virtual Studio",
						permissionRequired: "write",
					},
					name: "Virtual Studio Intro",
					component: () =>
						import("@/views/owner/[owner]/w/[workspace]/studio.vue"),
				},
				{
					path: "analytics",
					meta: { authRequired: true, title: "Analytics" },
					name: "Analytics Home",
					component: () =>
						import("@/views/owner/[owner]/w/[workspace]/analytics.vue"),
				},
			],
		},
	],
});

router.beforeResolve(async (to, _from, next) => {
	const user = localStorage.getItem("oauth.user");
	const authStore = useAuthStore();
	if (!authStore.user) {
		authStore.setUser(JSON.parse(user as string));
	}

	if (to.name !== "Login" && to.meta?.authRequired && !user) {
		next({ name: "Login", query: { failed: to.fullPath } });
		return;
	}

	const workspacesStore = useWorkspacesStore();
	if (user && to.name !== "Login" && !workspacesStore.fetched) {
		await workspacesStore.fetchWorkspaces();
	}

	next();
});

router.afterEach((to) => {
	useTopBarStore().canGoBack = Boolean(router.options.history.state.back);
	useTopBarStore().canGoForward = Boolean(router.options.history.state.forward);

	const metaTitle = to.meta.title as string;
	const dynamicTitle = metaTitle.replace(
		/:([a-zA-Z]+)/g,
		(_, key) => to.params[key] || key,
	); // Replace dynamic params

	document.title = `${dynamicTitle} | Dopple` || "Dashboard | Dopple";
});

export default router;
